import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Megaphone, Save } from "lucide-react";
import api, { formatApiError } from "../api/client";
import { AdminTabs } from "./AdminDashboard";

const EMPTY = {
    headline: "",
    body: "",
    image_url: "",
    cta_label: "",
    cta_url: "",
    placement: "timeline",
    enabled: true,
    priority: 0,
};

export default function AdminAds() {
    const [ads, setAds] = useState([]);
    const [draft, setDraft] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/ads");
            setAds(r.data || []);
        } catch (e) {
            toast.error("failed to load ads", { description: formatApiError(e.response?.data?.detail) });
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!draft.headline.trim()) {
            toast.error("Headline is required");
            return;
        }
        setBusy(true);
        try {
            await api.post("/admin/ads", { ...draft, priority: parseInt(draft.priority, 10) || 0 });
            toast.success("Ad created");
            setDraft(EMPTY);
            load();
        } catch (e) {
            toast.error("create failed", { description: formatApiError(e.response?.data?.detail) });
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (ad) => {
        try {
            await api.patch(`/admin/ads/${ad.id}`, { enabled: !ad.enabled });
            load();
        } catch (e) {
            toast.error("toggle failed", { description: formatApiError(e.response?.data?.detail) });
        }
    };

    const remove = async (ad) => {
        if (!window.confirm(`Delete ad "${ad.headline}"?`)) return;
        try {
            await api.delete(`/admin/ads/${ad.id}`);
            toast.success("Ad deleted");
            load();
        } catch (e) {
            toast.error("delete failed", { description: formatApiError(e.response?.data?.detail) });
        }
    };

    const update = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    return (
        <div className="bg-[#050505] min-h-[calc(100vh-3.5rem)] bg-grid-fine">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 grid gap-6">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400">Admin · Promotional Feed</div>
                        <h1 className="text-2xl sm:text-3xl font-medium tracking-tighter mt-2 flex items-center gap-3">
                            <Megaphone className="w-6 h-6 text-cyan-400" /> Ads in snapshot timelines
                        </h1>
                        <p className="text-xs sm:text-sm text-zinc-500 mt-2 max-w-2xl">
                            Ads placed here are interleaved into every trader's minute-by-minute snapshot timeline
                            (one card per ~10 rows). Use them for announcements, promotions, or partner spots.
                        </p>
                    </div>
                    <Link
                        to="/admin"
                        data-testid="admin-ads-back"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-zinc-400 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-2 rounded-sm"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> Admin home
                    </Link>
                </div>

                <AdminTabs />

                {/* Create form */}
                <form
                    onSubmit={save}
                    data-testid="admin-ad-form"
                    className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm p-5 sm:p-6 grid gap-4"
                >
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">New ad</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Headline (required, max 120)">
                            <input
                                data-testid="ad-headline-input"
                                value={draft.headline}
                                onChange={(e) => update("headline", e.target.value)}
                                maxLength={120}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                                placeholder="e.g. 0% entry fees this week on BTC"
                                required
                            />
                        </Field>
                        <Field label="Priority (higher shown first)">
                            <input
                                data-testid="ad-priority-input"
                                type="number"
                                value={draft.priority}
                                onChange={(e) => update("priority", e.target.value)}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                            />
                        </Field>
                    </div>

                    <Field label="Body (optional, max 400)">
                        <textarea
                            data-testid="ad-body-input"
                            value={draft.body}
                            onChange={(e) => update("body", e.target.value)}
                            maxLength={400}
                            rows={2}
                            className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-xs resize-none"
                            placeholder="Two-line teaser shown under the headline."
                        />
                    </Field>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Image URL (optional, square thumbnail)">
                            <input
                                data-testid="ad-image-input"
                                value={draft.image_url}
                                onChange={(e) => update("image_url", e.target.value)}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                                placeholder="https://…"
                            />
                        </Field>
                        <Field label="Placement">
                            <select
                                data-testid="ad-placement-input"
                                value={draft.placement}
                                onChange={(e) => update("placement", e.target.value)}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                            >
                                <option value="timeline">timeline (snapshot history)</option>
                                <option value="positions">positions panel</option>
                                <option value="fees">fees page</option>
                            </select>
                        </Field>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="CTA label (optional)">
                            <input
                                data-testid="ad-cta-label-input"
                                value={draft.cta_label}
                                onChange={(e) => update("cta_label", e.target.value)}
                                maxLength={40}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                                placeholder="Trade now"
                            />
                        </Field>
                        <Field label="CTA URL (path or full https://)">
                            <input
                                data-testid="ad-cta-url-input"
                                value={draft.cta_url}
                                onChange={(e) => update("cta_url", e.target.value)}
                                className="w-full bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                                placeholder="/terminal or https://verslan.xyz/promo"
                            />
                        </Field>
                    </div>

                    <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                        <label className="flex items-center gap-2 text-xs font-mono text-zinc-400">
                            <input
                                data-testid="ad-enabled-input"
                                type="checkbox"
                                checked={draft.enabled}
                                onChange={(e) => update("enabled", e.target.checked)}
                                className="accent-cyan-400"
                            />
                            Enabled (visible to traders)
                        </label>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="ad-submit-button"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-widest rounded-sm"
                        >
                            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Create ad"}
                        </button>
                    </div>
                </form>

                {/* List */}
                <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                    <div className="px-4 sm:px-5 py-3 border-b border-[#1F1F24] flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                            All ads ({ads.length})
                        </div>
                    </div>
                    {ads.length === 0 && (
                        <div className="px-5 py-10 text-center text-[11px] font-mono text-zinc-600">
                            No ads yet. Create one above — it will appear in every trader's snapshot timeline.
                        </div>
                    )}
                    <div className="divide-y divide-[#1F1F24]">
                        {ads.map((ad) => (
                            <div
                                key={ad.id}
                                data-testid={`admin-ad-row-${ad.id}`}
                                className="px-4 sm:px-5 py-4 grid sm:grid-cols-[1fr_auto] gap-3 items-start"
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-[9px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded-sm ${ad.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-700/30 text-zinc-500"}`}>
                                            {ad.enabled ? "live" : "off"}
                                        </span>
                                        <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                                            {ad.placement}
                                        </span>
                                        <span className="text-[9px] font-mono text-zinc-600">priority {ad.priority}</span>
                                        <span className="text-[9px] font-mono text-zinc-600">{ad.impressions || 0} impr</span>
                                        <span className="text-[9px] font-mono text-zinc-600">{ad.clicks || 0} clicks</span>
                                        <span className="text-[9px] font-mono text-cyan-400/80">
                                            CTR {(ad.impressions || 0) === 0
                                                ? "—"
                                                : (((ad.clicks || 0) / ad.impressions) * 100).toFixed(2) + "%"}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-sm font-medium text-zinc-100 truncate">{ad.headline}</div>
                                    {ad.body && <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{ad.body}</div>}
                                    {ad.cta_url && (
                                        <div className="text-[10px] font-mono text-cyan-400 mt-1 truncate">
                                            CTA · {ad.cta_label || "Learn more"} → {ad.cta_url}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button
                                        data-testid={`ad-toggle-${ad.id}`}
                                        onClick={() => toggle(ad)}
                                        className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 hover:text-cyan-400 border border-[#1F1F24] hover:border-cyan-500/40 px-3 py-1.5 rounded-sm"
                                    >
                                        {ad.enabled ? "Disable" : "Enable"}
                                    </button>
                                    <button
                                        data-testid={`ad-delete-${ad.id}`}
                                        onClick={() => remove(ad)}
                                        className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-rose-400 hover:text-rose-300 border border-rose-500/30 hover:border-rose-500/60 px-3 py-1.5 rounded-sm"
                                    >
                                        <Trash2 className="w-3 h-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div className="grid gap-1.5">
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</label>
            {children}
        </div>
    );
}
